import { useEffect, useRef, useState } from 'react'
import { SlidePreview } from '@/components/Deals/ProposalStudio/SlidePreview'
import { ROCKET_STANDARD_SLIDES } from '@/components/Deals/ProposalStudio/rocketStandardSlides'
import type { ProposalValueSlide } from '@/utils/proposalPresentationSnapshot'
import { RocketPdfSlide } from './RocketPdfSlide'

const DESIGN_WIDTH = 880

type ProposalPresentationFrameProps = {
  index: number
  value: ProposalValueSlide
  immersive: boolean
  onAdvance: (direction: 1 | -1) => void
}

export function ProposalPresentationFrame({
  index,
  value,
  immersive,
  onAdvance,
}: ProposalPresentationFrameProps) {
  const standard = ROCKET_STANDARD_SLIDES[index]
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const node = frameRef.current
    if (!node || standard) return
    const update = () => setScale(Math.max(0.2, node.clientWidth / DESIGN_WIDTH))
    update()
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [standard, index])

  return (
    <div
      ref={frameRef}
      className={`relative w-full overflow-hidden bg-[#070712] ${
        immersive ? 'h-full' : 'aspect-video rounded-xl shadow-2xl'
      }`}
      onClick={
        immersive
          ? (event) => {
              const bounds = event.currentTarget.getBoundingClientRect()
              const direction = event.clientX - bounds.left < bounds.width * 0.28 ? -1 : 1
              onAdvance(direction)
            }
          : undefined
      }
    >
      {standard ? (
        <RocketPdfSlide pageNumber={standard.number} />
      ) : (
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 [&>div>p]:hidden"
          style={{
            width: DESIGN_WIDTH,
            height: Math.round((DESIGN_WIDTH * 9) / 16),
            transform: `translate(-50%, -50%) scale(${scale})`,
          }}
        >
          <SlidePreview
            layout={value.layout}
            onLayoutChange={() => undefined}
            planName={value.planName}
            planDescription={value.planDescription}
            supportHoursLabel={value.supportHoursLabel}
            supportTierLines={value.supportTierLines}
            planTipo={value.planTipo}
            projectType={value.projectType}
            planValue={value.planValue}
            isRecurring={value.isRecurring}
            hidePricing={value.hidePricing}
            priceCompareFrom={value.priceCompareFrom}
            paymentDetails={value.paymentDetails}
            loyaltyDuration={value.loyaltyDuration}
            negotiationDeadlineLabel={value.negotiationDeadlineLabel}
            bonusText={value.bonusText}
            serviceGroups={new Map(value.serviceGroups)}
          />
        </div>
      )}
    </div>
  )
}
